import { useState } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import ThemeAwareLogo from "./ThemeAwareLogo";
import { useAuth } from "../contexts/AuthContext";
import { OrganizationSelector } from "./OrganizationSelector";

// Define icons for navigation items
const DashboardIcon = () => (
  <svg
    className="w-5 h-5"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"></path>
  </svg>
);

const InventoryIcon = () => (
  <svg
    className="w-5 h-5"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"></path>
  </svg>
);

const JobsIcon = () => (
  <svg
    className="w-5 h-5"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
      clipRule="evenodd"
    ></path>
    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"></path>
  </svg>
);

const PicklistIcon = () => (
  <svg
    className="w-5 h-5"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
    <path
      fillRule="evenodd"
      d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
      clipRule="evenodd"
    ></path>
  </svg>
);

const CheckInIcon = () => (
  <svg
    className="w-5 h-5"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    ></path>
  </svg>
);

const CalendarIcon = () => (
  <svg
    className="w-5 h-5"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
      clipRule="evenodd"
    ></path>
  </svg>
);

const CrewIcon = () => (
  <svg
    className="w-5 h-5"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path>
  </svg>
);

const SettingsIcon = () => (
  <svg
    className="w-5 h-5"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
      clipRule="evenodd"
    ></path>
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    className="w-4 h-4 transition-transform duration-200"
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    ></path>
  </svg>
);

interface SidebarProps {
  collapsed?: boolean;
  setCollapsed?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Sidebar({
  collapsed = false,
  setCollapsed,
}: SidebarProps) {
  const location = useLocation();
  const { orgId } = useParams();
  const { currentOrganization } = useAuth();
  const [expandedDropdowns, setExpandedDropdowns] = useState<string[]>([]);

  // Toggle dropdown expansion
  const toggleDropdown = (dropdownName: string) => {
    setExpandedDropdowns((prev) =>
      prev.includes(dropdownName)
        ? prev.filter((name) => name !== dropdownName)
        : [...prev, dropdownName]
    );
  };

  // Check if dropdown is expanded
  const isDropdownExpanded = (dropdownName: string) => {
    return expandedDropdowns.includes(dropdownName);
  };

  // Define nav items with icons if we have an organization
  const navItems = currentOrganization
    ? [
        {
          name: "Dashboard",
          path: `/org/${currentOrganization.id}/dashboard`,
          icon: DashboardIcon,
          type: "link" as const,
        },
        {
          name: "Inventory",
          path: `/org/${currentOrganization.id}/inventory`,
          icon: InventoryIcon,
          type: "link" as const,
        },
        {
          name: "Jobs",
          icon: JobsIcon,
          type: "dropdown" as const,
          children: [
            {
              name: "All Jobs",
              path: `/org/${currentOrganization.id}/jobs`,
              icon: JobsIcon,
            },
            {
              name: "Picklist",
              path: `/org/${currentOrganization.id}/picklist`,
              icon: PicklistIcon,
            },
            {
              name: "Check In",
              path: `/org/${currentOrganization.id}/checkin`,
              icon: CheckInIcon,
            },
          ],
        },
        {
          name: "Calendar",
          path: `/org/${currentOrganization.id}/calendar`,
          icon: CalendarIcon,
          type: "link" as const,
        },
        {
          name: "Crew",
          path: `/org/${currentOrganization.id}/crew`,
          icon: CrewIcon,
          type: "link" as const,
        },
      ]
    : [];

  // Settings item defined separately to make it sticky at bottom
  const settingsItem = currentOrganization
    ? {
        name: "Settings",
        path: `/org/${currentOrganization.id}/settings`,
        icon: SettingsIcon,
      }
    : null;

  const toggleCollapsed = () => {
    if (setCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  // Check if the current path is the root or dashboard
  const isRootOrDashboard = (path: string) => {
    return (
      location.pathname === "/" ||
      location.pathname === `/org/${orgId}` ||
      location.pathname === `/org/${orgId}/dashboard` ||
      path === location.pathname
    );
  };

  return (
    <aside
      className={`bg-dark text-white flex flex-col h-full shadow-lg transition-all duration-300 ${
        collapsed ? "w-20" : "w-64"
      }`}
    >
      {/* Logo section */}
      <div className="pt-6 pb-4 px-4 flex justify-center">
        <div className={`${collapsed ? "w-14" : "w-full max-w-[160px]"}`}>
          <ThemeAwareLogo className="h-auto w-full" />
        </div>
      </div>

      {/* Organization Selector */}
      {currentOrganization && !collapsed && (
        <div className="px-4 mb-6">
          <OrganizationSelector variant="sidebar" />
        </div>
      )}

      {/* Collapsed Organization Display */}
      {currentOrganization && collapsed && (
        <div className="px-2 mb-6 text-center">
          {currentOrganization.logo_url ? (
            <img
              src={currentOrganization.logo_url}
              alt={currentOrganization.name}
              className="w-10 h-10 mx-auto rounded-full border-2 border-primary"
              title={currentOrganization.name}
            />
          ) : (
            <div
              className="w-10 h-10 mx-auto rounded-full flex items-center justify-center bg-primary text-white font-medium"
              title={currentOrganization.name}
            >
              {currentOrganization.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-2 pb-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900">
        <div
          className={`${
            collapsed ? "px-2" : "px-4"
          } py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider`}
        >
          {!collapsed && "Navigation"}
        </div>
        <ul className="space-y-1">
          {navItems.map((item) => {
            if (item.type === "link") {
              // Regular navigation link

              return (
                <li key={item.name}>
                  <NavLink
                    to={item.path}
                    preventScrollReset={true}
                    className={({ isActive: linkActive }) =>
                      `flex items-center ${
                        collapsed ? "justify-center px-2" : "px-4"
                      } py-3 rounded-lg transition-all duration-200 ${
                        (item.name === "Dashboard" &&
                          isRootOrDashboard(item.path)) ||
                        linkActive
                          ? "bg-primary text-white font-medium"
                          : "text-gray-300 hover:bg-gray-800 hover:text-white"
                      }`
                    }
                    title={collapsed ? item.name : undefined}
                  >
                    <span className="flex-shrink-0">
                      <item.icon />
                    </span>
                    {!collapsed && <span className="ml-3">{item.name}</span>}
                    {!collapsed &&
                      ((item.name === "Dashboard" &&
                        isRootOrDashboard(item.path)) ||
                        location.pathname === item.path) && (
                        <span className="ml-auto">
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                        </span>
                      )}
                  </NavLink>
                </li>
              );
            } else if (item.type === "dropdown") {
              // Dropdown navigation item

              const isExpanded = isDropdownExpanded(item.name);

              return (
                <li key={item.name}>
                  <button
                    onClick={() => toggleDropdown(item.name)}
                    className={`w-full flex items-center ${
                      collapsed ? "justify-center px-2" : "px-4"
                    } py-3 rounded-lg transition-all duration-200 text-gray-300 hover:bg-gray-800 hover:text-white`}
                    title={collapsed ? item.name : undefined}
                  >
                    <span className="flex-shrink-0">
                      <item.icon />
                    </span>
                    {!collapsed && (
                      <>
                        <span className="ml-3 flex-1 text-left">
                          {item.name}
                        </span>
                        <span
                          className={`ml-auto transition-transform duration-200 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        >
                          <ChevronDownIcon />
                        </span>
                      </>
                    )}
                  </button>

                  {/* Dropdown children */}
                  {!collapsed && isExpanded && (
                    <ul className="mt-1 ml-4 space-y-1">
                      {item.children.map((child) => {
                        return (
                          <li key={child.name}>
                            <NavLink
                              to={child.path}
                              preventScrollReset={true}
                              className={({ isActive: linkActive }) =>
                                `flex items-center px-4 py-2 rounded-lg transition-all duration-200 text-sm ${
                                  linkActive
                                    ? "bg-primary/20 text-primary font-medium"
                                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                                }`
                              }
                            >
                              <span className="flex-shrink-0">
                                <child.icon />
                              </span>
                              <span className="ml-3">{child.name}</span>
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            }

            return null;
          })}
        </ul>
      </nav>

      {/* Settings - Sticky at bottom */}
      {settingsItem && (
        <div className="mt-auto border-t border-gray-700 pt-2 pb-4 px-2">
          <NavLink
            to={settingsItem.path}
            preventScrollReset={true}
            className={({ isActive }) =>
              `flex items-center ${
                collapsed ? "justify-center px-2" : "px-4"
              } py-3 rounded-lg transition-all duration-200 ${
                isActive
                  ? "bg-gradient-to-r from-primary to-primary/80 text-white font-medium shadow-lg"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white"
              }`
            }
            title={collapsed ? settingsItem.name : undefined}
          >
            <span className="flex-shrink-0">
              <settingsItem.icon />
            </span>
            {!collapsed && (
              <span className="ml-3 font-medium">{settingsItem.name}</span>
            )}
            {!collapsed && location.pathname === settingsItem.path && (
              <span className="ml-auto">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </span>
            )}
          </NavLink>
        </div>
      )}

      {/* Collapse toggle button */}
      <button
        onClick={toggleCollapsed}
        className="mt-auto mb-4 mx-auto flex items-center justify-center w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          {collapsed ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 5l7 7-7 7M5 5l7 7-7 7"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          )}
        </svg>
      </button>
    </aside>
  );
}
